// src/components/appSettings/Config/ConfigForm.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchConfigByIdThunk,
  createAppConfigThunk,
  updateAppConfigThunk,
} from "../../../store/thunks/appSettingsThunks";
import Button from "../../../components/common/Button";
import toast from "react-hot-toast";
import { FiInfo, FiAlertCircle } from "react-icons/fi";

const ConfigForm = ({ mode = "add" }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { selectedConfig, loading } = useSelector((state) => state.appSettings);

  const [formData, setFormData] = useState({
    key: "",
    name: "",
    description: "",
    category: "GENERAL",
    dataType: "STRING",
    value: "",
    defaultValue: "",
    unit: "",
    isRequired: false,
    isSecret: false,
    validation: {
      min: "",
      max: "",
      pattern: "",
      allowedValues: [],
    },
    metadata: {
      group: "",
      order: 0,
      helpText: "",
      example: "",
    },
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Load config for edit mode
  useEffect(() => {
    if (mode === "edit" && id) {
      dispatch(fetchConfigByIdThunk(id));
    }
  }, [mode, id, dispatch]);

  // Update form when data loads
  useEffect(() => {
    if (mode === "edit" && selectedConfig) {
      setFormData({
        key: selectedConfig.key || "",
        name: selectedConfig.name || "",
        description: selectedConfig.description || "",
        category: selectedConfig.category || "GENERAL",
        dataType: selectedConfig.dataType || "STRING",
        value:
          selectedConfig.dataType === "JSON"
            ? JSON.stringify(selectedConfig.value, null, 2)
            : selectedConfig.value?.toString() || "",
        defaultValue: selectedConfig.defaultValue?.toString() || "",
        unit: selectedConfig.unit || "",
        isRequired: selectedConfig.isRequired || false,
        isSecret: selectedConfig.isSecret || false,
        validation: {
          min: selectedConfig.validation?.min?.toString() || "",
          max: selectedConfig.validation?.max?.toString() || "",
          pattern: selectedConfig.validation?.pattern || "",
          allowedValues: selectedConfig.validation?.allowedValues || [],
        },
        metadata: {
          group: selectedConfig.metadata?.group || "",
          order: selectedConfig.metadata?.order || 0,
          helpText: selectedConfig.metadata?.helpText || "",
          example: selectedConfig.metadata?.example || "",
        },
      });
    }
  }, [mode, selectedConfig]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === "checkbox" ? checked : value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.key || formData.key.length < 2) {
      newErrors.key = "Key is required (min 2 characters)";
    }

    if (!formData.name || formData.name.length < 3) {
      newErrors.name = "Name is required (min 3 characters)";
    }

    if (!formData.value && formData.isRequired) {
      newErrors.value = "Value is required for required configs";
    }

    // Validate JSON if dataType is JSON
    if (formData.dataType === "JSON" && formData.value) {
      try {
        JSON.parse(formData.value);
      } catch (e) {
        newErrors.value = "Invalid JSON format";
      }
    }

    // Validate number if dataType is NUMBER
    if (formData.dataType === "NUMBER" && formData.value) {
      if (isNaN(formData.value)) {
        newErrors.value = "Value must be a valid number";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    setSubmitting(true);

    try {
      let parsedValue = formData.value;

      // Parse value based on data type
      if (formData.dataType === "NUMBER") {
        parsedValue = parseFloat(formData.value);
      } else if (formData.dataType === "BOOLEAN") {
        parsedValue = formData.value === "true" || formData.value === true;
      } else if (formData.dataType === "JSON") {
        parsedValue = JSON.parse(formData.value);
      } else if (formData.dataType === "ARRAY") {
        parsedValue = Array.isArray(formData.value)
          ? formData.value
          : formData.value.split(",").map((v) => v.trim());
      }

      const submitData = {
        ...formData,
        value: parsedValue,
        validation: {
          min: formData.validation.min
            ? parseFloat(formData.validation.min)
            : undefined,
          max: formData.validation.max
            ? parseFloat(formData.validation.max)
            : undefined,
          pattern: formData.validation.pattern || undefined,
          allowedValues:
            formData.validation.allowedValues.length > 0
              ? formData.validation.allowedValues
              : undefined,
        },
        metadata: {
          group: formData.metadata.group || undefined,
          order: parseInt(formData.metadata.order) || 0,
          helpText: formData.metadata.helpText || undefined,
          example: formData.metadata.example || undefined,
        },
      };

      if (mode === "add") {
        await dispatch(createAppConfigThunk(submitData)).unwrap();
        toast.success("Configuration created successfully");
      } else {
        await dispatch(
          updateAppConfigThunk({
            id: selectedConfig._id,
            configData: submitData,
          })
        ).unwrap();
        toast.success("Configuration updated successfully");
      }

      navigate("/app-settings/config");
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(error.message || `Failed to ${mode} configuration`);
    } finally {
      setSubmitting(false);
    }
  };

  if (mode === "edit" && loading && !selectedConfig) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {mode === "add" ? "Add Configuration" : "Edit Configuration"}
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          {mode === "add"
            ? "Create a new application configuration"
            : `Editing: ${selectedConfig?.name || "Configuration"}`}
        </p>
      </div>

      {/* Basic Information */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium mb-4">Basic Information</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Configuration Key *
            </label>
            <input
              type="text"
              name="key"
              value={formData.key}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                errors.key ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="config_key_name"
              disabled={mode === "edit"}
            />
            {errors.key && (
              <p className="mt-1 text-sm text-red-500">{errors.key}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Unique identifier (cannot be changed after creation)
            </p>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Display Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                errors.name ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Friendly name"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-500">{errors.name}</p>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Brief description of this configuration"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="GENERAL">General</option>
              <option value="PRICING">Pricing</option>
              <option value="RATES">Rates</option>
              <option value="PAYMENT">Payment</option>
              <option value="DELIVERY">Delivery</option>
              <option value="LIMITS">Limits</option>
              <option value="FEATURES">Features</option>
              <option value="BUSINESS">Business</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data Type
            </label>
            <select
              name="dataType"
              value={formData.dataType}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="STRING">String</option>
              <option value="NUMBER">Number</option>
              <option value="BOOLEAN">Boolean</option>
              <option value="JSON">JSON Object</option>
              <option value="ARRAY">Array</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Unit (Optional)
            </label>
            <input
              type="text"
              name="unit"
              value={formData.unit}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="e.g., ₹, %, kg, days"
            />
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center mt-6">
              <input
                type="checkbox"
                name="isRequired"
                checked={formData.isRequired}
                onChange={handleChange}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">
                Required
              </span>
            </label>

            <label className="flex items-center mt-6">
              <input
                type="checkbox"
                name="isSecret"
                checked={formData.isSecret}
                onChange={handleChange}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">
                Secret (Hide Value)
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Value Configuration */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium mb-4">Value Configuration</h3>

        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current Value {formData.isRequired && "*"}
            </label>
            {formData.dataType === "BOOLEAN" ? (
              <select
                name="value"
                value={formData.value}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                  errors.value ? "border-red-500" : "border-gray-300"
                }`}
              >
                <option value="true">True</option>
                <option value="false">False</option>
              </select>
            ) : formData.dataType === "JSON" ? (
              <textarea
                name="value"
                value={formData.value}
                onChange={handleChange}
                rows="8"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm ${
                  errors.value ? "border-red-500" : "border-gray-300"
                }`}
                placeholder='{"key": "value"}'
              />
            ) : (
              <input
                type={
                  formData.isSecret
                    ? "password"
                    : formData.dataType === "NUMBER"
                    ? "number"
                    : "text"
                }
                name="value"
                value={formData.value}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                  errors.value ? "border-red-500" : "border-gray-300"
                }`}
                placeholder={`Enter ${formData.dataType.toLowerCase()} value`}
              />
            )}
            {errors.value && (
              <p className="mt-1 text-sm text-red-500">{errors.value}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Default Value
            </label>
            <input
              type="text"
              name="defaultValue"
              value={formData.defaultValue}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Fallback value"
            />
          </div>
        </div>
      </div>

      {/* Validation Rules */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium mb-4">Validation Rules</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {formData.dataType === "NUMBER" && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Minimum Value
                </label>
                <input
                  type="number"
                  name="validation.min"
                  value={formData.validation.min}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Maximum Value
                </label>
                <input
                  type="number"
                  name="validation.max"
                  value={formData.validation.max}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </>
          )}

          {formData.dataType === "STRING" && (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pattern (Regex)
              </label>
              <input
                type="text"
                name="validation.pattern"
                value={formData.validation.pattern}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="^[a-zA-Z0-9]+$"
              />
            </div>
          )}

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Allowed Values (comma separated)
            </label>
            <input
              type="text"
              value={formData.validation.allowedValues.join(", ")}
              onChange={(e) => {
                const values = e.target.value
                  .split(",")
                  .map((v) => v.trim())
                  .filter(Boolean);
                setFormData((prev) => ({
                  ...prev,
                  validation: {
                    ...prev.validation,
                    allowedValues: values,
                  },
                }));
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="value1, value2, value3"
            />
          </div>
        </div>
      </div>

      {/* Metadata */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium mb-4">Additional Information</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Group
            </label>
            <input
              type="text"
              name="metadata.group"
              value={formData.metadata.group}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="e.g., Gold Rates, Payment Settings"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Display Order
            </label>
            <input
              type="number"
              name="metadata.order"
              value={formData.metadata.order}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Help Text
            </label>
            <textarea
              name="metadata.helpText"
              value={formData.metadata.helpText}
              onChange={handleChange}
              rows="2"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Additional guidance for this configuration"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Example Value
            </label>
            <input
              type="text"
              name="metadata.example"
              value={formData.metadata.example}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Example: 5000"
            />
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 rounded-lg p-4 flex items-start gap-3">
        <FiInfo className="text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-800">
          <p className="font-medium mb-1">Configuration Best Practices</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Use descriptive keys and names</li>
            <li>Set appropriate validation rules for data integrity</li>
            <li>Mark sensitive data as secret</li>
            <li>Provide clear descriptions and help text</li>
            <li>Group related configurations together</li>
          </ul>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end gap-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate("/app-settings/config")}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button type="submit" loading={submitting} disabled={submitting}>
          {submitting
            ? "Saving..."
            : mode === "add"
            ? "Create Configuration"
            : "Update Configuration"}
        </Button>
      </div>
    </form>
  );
};

export default ConfigForm;
